import {Component, OnInit} from '@angular/core';
import {forkJoin, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {ReferralService} from '../../../shared/services/referral.service';
import {SettingService} from '../../../shared/services/setting.service';
import {UniversityService} from '../../../shared/services/university.service';
import {CourseService} from '../../../shared/services/course.service';

// A real university + course pair, as it actually exists on the backend.
interface CatalogProgram {
    name: string;
    uni: string;
}

interface ProgramDraft {
    p: CatalogProgram;
    reward: number;
    discount: number;
    active: boolean;
}

// Shape persisted to the generic `settings` row's referral_programs column (JSON). Read by the
// public /referral-program page's "Rewards by program" table (getReferralProgramRewardsServerSide
// in the Next.js app) via GET /settings.
interface StoredProgramReward {
    program: string;
    university: string;
    partner_reward: number;
    student_discount: number;
    show_on_site: boolean;
}

@Component({
    selector: 'app-referral-programs',
    templateUrl: './referral-programs.component.html'
})
export class ReferralProgramsComponent implements OnInit {
    rows: ProgramDraft[] = [];
    submitted = false;
    loading = false;

    constructor(private referralService: ReferralService,
                private settingService: SettingService,
                private universityService: UniversityService,
                private courseService: CourseService) {
    }

    ngOnInit() {
        this.load();
    }

    load() {
        this.loading = true;
        forkJoin({
            catalog: this.loadCatalog(),
            stored: this.settingService.detail().pipe(
                map((data: any) => this.parseStored(data?.referral_programs)),
                catchError(() => of([] as StoredProgramReward[]))
            )
        }).subscribe({
            next: ({catalog, stored}) => {
                this.rows = this.buildRows(catalog, stored);
                this.loading = false;
            },
            error: () => {
                this.loading = false;
            }
        });
    }

    // Real tied-up (partner) universities, each joined with the real courses offered at them.
    // Untied universities are listed in the catalog but don't pay partner commissions, so they're
    // excluded here the same way the public refer-and-earn page should exclude them.
    private loadCatalog() {
        return this.universityService.get({page: -1, per_page: 500}).pipe(
            map((data: any) => (data && data['data']) ? data['data'] : []),
            map((unis: any[]) => unis.filter(u => u && u.id && u.status === 'ACTIVE' && u.tied_up === 'ACTIVE')),
            switchMap((unis: any[]) => {
                if (unis.length === 0) {
                    return of([] as CatalogProgram[][]);
                }
                return forkJoin(unis.map(u => this.courseService.getByAdmin(u.id, {page: -1}).pipe(
                    map((data: any) => (data && data['data']) ? data['data'] : []),
                    map((courses: any[]) => courses.map(c => ({
                        name: c.name || c.short_name,
                        uni: u.name
                    }))),
                    catchError(() => of([] as CatalogProgram[]))
                )));
            }),
            map((lists: CatalogProgram[][]) => ([] as CatalogProgram[]).concat(...lists)),
            catchError(() => of([] as CatalogProgram[]))
        );
    }

    private buildRows(catalog: CatalogProgram[], stored: StoredProgramReward[]): ProgramDraft[] {
        return catalog.map(p => {
            const match = stored.find(s => s.program === p.name && s.university === p.uni);
            return {
                p,
                reward: match ? match.partner_reward : 0,
                discount: match ? match.student_discount : 0,
                active: match ? match.show_on_site : false
            };
        });
    }

    private parseStored(raw: any): StoredProgramReward[] {
        if (!raw) return [];
        try {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    invalid(v: any): boolean {
        return v === null || v === '' || v === undefined || +v < 0;
    }

    save() {
        this.submitted = true;
        if (this.rows.some(r => this.invalid(r.reward) || this.invalid(r.discount))) {
            this.referralService.toast('Enter ₹0 or more in every reward field');
            return;
        }
        this.submitted = false;

        const payload: StoredProgramReward[] = this.rows.map(r => ({
            program: r.p.name,
            university: r.p.uni,
            partner_reward: +r.reward,
            student_discount: +r.discount,
            show_on_site: r.active
        }));

        this.settingService.update({referral_programs: JSON.stringify(payload)}).subscribe(() => {
            this.referralService.toast('New referrals use the updated rewards');
        });
    }
}
